<template>
  <div :class="containerStyles" ref="projectGridRef">
    <project-card
      v-for="(project, index) in projects"
      :key="project.title"
      :image="project.image"
      :imgAlt="project.imgAlt"
      :client="project.client"
      :title="project.title"
      :tag="project.tag"
      :country="project.country"
      :theme="project.theme"
      :href="project.href"
      :isUmbraco="isUmbraco"
      :index="index"
      :classes="getCols(index)"
    />
  </div>
</template>

<script setup lang="ts">
import { Ref, computed, onMounted, onUnmounted, ref, toRefs, watch } from "vue";
import { Theme } from "@/types/types";
import ProjectCard from "@molecules/ProjectCard/ProjectCard.vue";
import { onIntersect } from "@/composables/onIntersect";
import { changeTheme } from "@/util/helper";

export interface IProject {
  image: string;
  imgAlt: string;
  client: string;
  title: string;
  tag: string;
  href: string;
  country: string;
  theme?: Theme | null;
}

export interface IProjectsProps {
  projects?: IProject[];
  isUmbraco: boolean;
  classes?: string;
  theme: Theme | null;
}

const props = defineProps<IProjectsProps>();

const { projects, classes, isUmbraco, theme } = toRefs(props);
const observer: Ref<IntersectionObserver | null> = ref(null);
const projectGridRef = ref(null);

const onEnter = () => {
  if (!theme.value) return;
  changeTheme(theme.value);
};
const onExit = () => {};

onMounted(() => {
  observer.value = onIntersect(projectGridRef.value, onEnter, onExit, false, {
    threshold: 0.6,
  });
});

onUnmounted(() => {
  if (!observer.value) return;
  observer.value.disconnect();
});

watch(theme, (value) => {
  if (!value) return;
  changeTheme(value);
});

const containerStyles = computed(() => {
  return `
  mb-20 lg:mb-[140px]
  lg:min-h-screen
  col-start-2 col-span-12 
  lg:col-start-3 lg:col-span-10 
  grid 
  grid-cols-12  
  gap-x-5 md:gap-x-8 lg:gap-x-12 2xl:gap-x-20 
  gap-y-[60px] lg:gap-y-[120px]`;
});

function getCols(index: number) {
  const patternIndex = index % 8;
  if (patternIndex === 0) {
    return "col-span-12 md:col-span-6";
  } else if (patternIndex === 1) {
    return "col-span-11 md:col-span-5 md:col-start-8";
  } else if (patternIndex === 2) {
    return "col-span-12 md:col-span-5";
  } else if (patternIndex === 3) {
    return "col-span-11 md:col-span-6 md:col-start-7";
  } else if (patternIndex === 4) {
    return "col-span-12 md:col-span-6";
  } else if (patternIndex === 5) {
    return "col-span-11 md:col-span-5 md:col-start-8";
  } else if (patternIndex === 7) {
    return "col-span-12 md:col-span-6 md:col-start-7";
  } else if (patternIndex === 6) {
    return "col-span-11 md:col-span-5";
  }
}
</script>
